import { useEffect } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "@rook/shared/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@rook/shared/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@rook/shared/components/ui/form";
import { Input } from "@rook/shared/components/ui/input";
import { usePageTitle } from "@rook/shared/lib/usePageTitle";

import { resetPassword } from "./api";
import { resetPasswordSchema, type ResetPasswordInput } from "./schemas";

export const ResetPasswordView = () => {
  usePageTitle("Reset password");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailFromQuery = searchParams.get("email") ?? "";

  const form = useForm<ResetPasswordInput>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { email: emailFromQuery, code: "", password: "" },
  });

  // Pre-fill email from URL on mount (and any subsequent param change
  // — rare, but cheap to handle).
  useEffect(() => {
    if (emailFromQuery) {
      form.setValue("email", emailFromQuery, { shouldValidate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailFromQuery]);

  const mutation = useMutation({
    mutationFn: resetPassword,
    onSuccess: () => {
      // Reset successful → bounce to login. The new password is in
      // the user's hands, no auto-login (security: confirms they
      // typed it correctly by logging in fresh).
      navigate("/", { replace: true });
    },
  });

  const errorMessage =
    mutation.error instanceof Error ? mutation.error.message : null;

  return (
    <Card>
      <CardHeader>
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
          Recover access
        </p>
        <CardTitle className="text-2xl">Reset password</CardTitle>
        <CardDescription>
          Paste the 6-character code from the recovery email and choose a new
          password.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {errorMessage && (
          <div
            role="alert"
            className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {errorMessage}
          </div>
        )}
        <Form {...form}>
          <form
            className="space-y-4"
            onSubmit={form.handleSubmit((data) => mutation.mutate(data))}
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email address</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      autoComplete="email"
                      placeholder="name@mail.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Verification code</FormLabel>
                  <FormControl>
                    <Input
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="6-character code"
                      maxLength={6}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="new-password"
                      placeholder="At least 8 characters, mix of upper, lower, digits"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" size="lg" disabled={mutation.isPending}>
              {mutation.isPending ? "Resetting…" : "Set new password"}
            </Button>
          </form>
        </Form>
        <p className="mt-6 text-center text-sm text-muted-foreground">
          <Link to="/" className="font-semibold text-rook-light hover:underline">
            Back to sign in
          </Link>
        </p>
      </CardContent>
    </Card>
  );
};
